/**
 * Public-site notification bell inbox (topbar dropdown).
 */
const inboxLogger = window.Logger
  ? window.Logger.create('notifications-inbox')
  : { debug: () => {}, warn: (...a) => console.warn(...a), error: console.error.bind(console) };

let panelOpen = false;
let clickOutsideHandler = null;
let escHandler = null;

// Leading glyph per notification type. Keep in sync with AccountManager.renderNotificationRow.
const NOTIFICATION_ICONS = {
  watch_price_drop: '📉',
  watch_back_in_stock: '🔄',
  watch_sold_out: '🚫',
  category_new_item: '🆕',
};

function escapeHtml(str) {
  if (str == null) return '';
  return String(str)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function formatRelativeTime(iso) {
  if (!iso) return '';
  const then = new Date(iso).getTime();
  const diff = Date.now() - then;
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(iso).toLocaleDateString();
}

function getAccountAlertsUrl() {
  const path = window.location.pathname || '';
  if (path.includes('/account') || path.includes('account.html')) {
    return '#alerts';
  }
  const link = document.querySelector('a[href*="account"]');
  if (link) {
    const href = link.getAttribute('href') || 'account.html';
    return `${href.split('#')[0]}#alerts`;
  }
  return 'account.html#alerts';
}

async function isAuthenticated() {
  if (window.accountManager?.getCurrentUser?.()) return true;
  try {
    if (window.apiService) {
      const profile = await window.apiService.getUserProfile();
      return !!profile;
    }
  } catch (_) { /* ignore */ }
  return false;
}

export async function refreshNotificationBadge() {
  const badge = document.getElementById('siteNotificationBadge');
  const pip = document.querySelector('.topbar-notifications-btn .pip, .topbar-notifications-link .pip');
  if (!badge && !pip) return;

  const authed = await isAuthenticated();
  if (!authed) {
    if (badge) {
      badge.textContent = '';
      badge.hidden = true;
    }
    if (pip) pip.classList.remove('has-unread');
    return;
  }

  try {
    const { count = 0 } = await window.apiService.getNotificationUnreadCount();
    const label = count > 99 ? '99+' : String(count);
    if (badge) {
      badge.textContent = count > 0 ? label : '';
      badge.hidden = count <= 0;
    }
    if (pip) pip.classList.toggle('has-unread', count > 0);
    const bell = document.getElementById('siteNotificationBellBtn') || document.querySelector('.topbar-notifications-link');
    if (bell) {
      bell.setAttribute('aria-label', count > 0 ? `Notifications, ${count} unread` : 'Notifications');
    }
  } catch (e) {
    inboxLogger.warn('Could not refresh notification badge', e);
  }
}

async function renderNotificationPanel() {
  const list = document.getElementById('siteNotificationList');
  if (!list || !window.apiService) return;

  list.innerHTML = '<div class="site-notification-loading">Loading…</div>';
  try {
    const data = await window.apiService.getNotifications({ limit: 8 });
    const items = data.notifications || [];
    if (!items.length) {
      list.innerHTML = '<div class="site-notification-empty"><p>No notifications yet</p><p class="site-notification-empty-hint">Follow categories to get alerts when new items are listed.</p></div>';
      return;
    }

    list.innerHTML = items.map((n) => {
      const payload = n.payload || {};
      const type = n.notificationType || n.notification_type || '';
      const img = payload.imageUrl || payload.image_url;
      const url = payload.productUrl || payload.product_url || '#';
      const unread = !n.isRead && !n.readAt;
      const isSoldOut = type === 'watch_sold_out';
      // Unknown types fall back to the generic icon rather than rendering blank.
      const icon = NOTIFICATION_ICONS[type] || '📦';
      const thumb = img
        ? `<img class="site-notification-thumb" src="${escapeHtml(img)}" alt="" loading="lazy" />`
        : `<span class="site-notification-thumb site-notification-thumb--placeholder" aria-hidden="true">${icon}</span>`;
      return `
        <a class="site-notification-item${unread ? ' is-unread' : ''}${isSoldOut ? ' is-inactive' : ''}" href="${escapeHtml(url)}" data-notification-id="${escapeHtml(n.id)}">
          ${thumb}
          <span class="site-notification-body">
            <span class="site-notification-title">${escapeHtml(n.title)}</span>
            <span class="site-notification-desc">${escapeHtml(n.body || '')}</span>
            <span class="site-notification-time">${escapeHtml(formatRelativeTime(n.createdAt || n.created_at))}</span>
          </span>
          ${unread ? '<span class="site-notification-dot" aria-label="Unread"></span>' : ''}
        </a>`;
    }).join('');

    list.querySelectorAll('[data-notification-id]').forEach((el) => {
      el.addEventListener('click', () => {
        const id = Number(el.getAttribute('data-notification-id'));
        if (!id) return;
        // Clicking navigates, which would cancel a normal fetch mid-flight and leave
        // the notification unread. keepalive lets it finish after unload.
        el.classList.remove('is-unread');
        el.querySelector('.site-notification-dot')?.remove();
        Promise.resolve(window.apiService.markNotificationsRead({ ids: [id], keepalive: true }))
          .then(() => refreshNotificationBadge())
          .catch((e) => inboxLogger.warn('mark read failed', e));
      });
    });
  } catch (e) {
    list.innerHTML = '<div class="site-notification-empty"><p>Could not load notifications</p></div>';
    inboxLogger.warn('render panel failed', e);
  }
}

function closeNotificationPanel() {
  const panel = document.getElementById('siteNotificationPanel');
  const btn = document.getElementById('siteNotificationBellBtn');
  if (panel) panel.hidden = true;
  if (btn) btn.setAttribute('aria-expanded', 'false');
  panelOpen = false;
  if (clickOutsideHandler) {
    document.removeEventListener('click', clickOutsideHandler);
    clickOutsideHandler = null;
  }
  if (escHandler) {
    document.removeEventListener('keydown', escHandler);
    escHandler = null;
  }
}

function openNotificationPanel() {
  const panel = document.getElementById('siteNotificationPanel');
  const btn = document.getElementById('siteNotificationBellBtn');
  if (!panel) return;
  panel.hidden = false;
  if (btn) btn.setAttribute('aria-expanded', 'true');
  panelOpen = true;
  renderNotificationPanel();

  clickOutsideHandler = (e) => {
    const wrap = document.getElementById('siteNotificationDropdown');
    if (wrap && !wrap.contains(e.target)) closeNotificationPanel();
  };
  setTimeout(() => document.addEventListener('click', clickOutsideHandler), 0);

  escHandler = (e) => {
    if (e.key === 'Escape') closeNotificationPanel();
  };
  document.addEventListener('keydown', escHandler);
}

async function toggleNotificationPanel(e) {
  e.preventDefault();
  e.stopPropagation();
  const authed = await isAuthenticated();
  if (!authed) {
    const loginUrl = document.body?.dataset?.railLoginUrl || 'login.html';
    window.location.href = loginUrl;
    return;
  }
  if (panelOpen) closeNotificationPanel();
  else openNotificationPanel();
}

function ensureNotificationMarkup() {
  const bellLink = document.querySelector('.topbar-notifications-link');
  if (!bellLink || document.getElementById('siteNotificationDropdown')) return;

  const wrap = document.createElement('div');
  wrap.id = 'siteNotificationDropdown';
  wrap.className = 'site-notification-dropdown';
  wrap.innerHTML = `
    <button type="button" class="icon-btn topbar-notifications-btn" id="siteNotificationBellBtn" aria-label="Notifications" aria-expanded="false" aria-haspopup="true">
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M6 8a6 6 0 0112 0c0 7 3 8 3 8H3s3-1 3-8z"/><path d="M10 21a2 2 0 004 0"/></svg>
      <span class="pip" aria-hidden="true"></span>
      <span class="site-notification-badge" id="siteNotificationBadge" hidden></span>
    </button>
    <div class="site-notification-panel" id="siteNotificationPanel" hidden role="region" aria-label="Notifications">
      <div class="site-notification-panel-header">
        <strong>Notifications</strong>
      </div>
      <div class="site-notification-list" id="siteNotificationList"></div>
      <a class="site-notification-view-all" href="${escapeHtml(getAccountAlertsUrl())}">View all</a>
    </div>`;

  bellLink.replaceWith(wrap);
  const btn = document.getElementById('siteNotificationBellBtn');
  if (btn) btn.addEventListener('click', toggleNotificationPanel);
}

export async function initNotificationsInbox() {
  ensureNotificationMarkup();
  await refreshNotificationBadge();
}

export async function migrateLocalCategoryFollows(userId) {
  if (!userId || !window.apiService) return;
  const key = 'usedfindr_followed_categories';
  const migratedKey = `usedfindr_followed_categories_migrated_${userId}`;
  try {
    if (localStorage.getItem(migratedKey) === '1') return;
    const raw = localStorage.getItem(key);
    if (!raw) {
      localStorage.setItem(migratedKey, '1');
      return;
    }
    const all = JSON.parse(raw);
    const ids = all[userId] || all[String(userId)] || [];
    if (ids.length) {
      await window.apiService.migrateLocalCategoryFollows(ids);
    }
    localStorage.setItem(migratedKey, '1');
  } catch (e) {
    inboxLogger.warn('localStorage category follow migration failed', e);
  }
}

if (typeof window !== 'undefined') {
  window.initNotificationsInbox = initNotificationsInbox;
  window.refreshNotificationBadge = refreshNotificationBadge;
  window.migrateLocalCategoryFollows = migrateLocalCategoryFollows;
}
